/*
  Сведения о помещении на карточке счёта - пункт 3.8 плана.

  Зачем это здесь. Площадь, количество комнат и этаж печатает финансово-лицевой
  счёт - лист, который идёт в суд приложением к заявлению (форма принята юристом
  18.09.2026). Основной путь их появления - выгрузка заказчика, вкладка
  «Помещения» в разделе «Загрузка». Но файл есть не у всех и не по каждому
  счёту, а пакет собирают уже сегодня: тогда недостающее вносят здесь.

  Пропуск называется словами, а не пустой клеткой: «нет в системе» - это то, что
  человек обязан увидеть до подачи, потому что в документе на этом месте окажется
  прочерк.

  Проверки границ делает сервер и только он: они те же, что у загрузки файлом, и
  вторая их копия на клиенте разошлась бы с первой молча.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { PREMISES_TYPE_TITLES } from '../api/format.js'
import { Field, Notice, Tag } from './Common.jsx'
import Select from './Select.jsx'

const FIELDS = [
  { key: 'area', label: 'Общая площадь, кв. м', placeholder: '55,9', decimal: true },
  { key: 'living_area', label: 'Жилая площадь, кв. м', placeholder: '30,3', decimal: true },
  { key: 'rooms', label: 'Комнат', placeholder: '2' },
  { key: 'floor', label: 'Этаж', placeholder: '9' },
]

/* Площадь показывается с запятой, как её пишут в документах и как кабинет
   показывает остальные числа. С сервера она приходит строкой «55.90»: копейку
   и десятую долю метра нельзя доверять числу с плавающей точкой. */
function shown(value, decimal) {
  if (value === null || value === undefined) return null
  return decimal ? String(value).replace('.', ',') : String(value)
}

/* Доля приходит с сервера с четырьмя знаками («0.5000»), потому что бывает
   и 1/3. Человеку хвостовые нули мешают: половина - это «0,5», а не «0,5000». */
function shownShare(value) {
  if (value === null || value === undefined) return null
  const trimmed = String(value).replace(/0+$/, '').replace(/\.$/, '')
  return trimmed.replace('.', ',')
}

function valuesOf(premises) {
  /* Пустое значение приходит с сервера как `null`, а поле ввода требует строку:
     без замены React считает поле неуправляемым и теряет начало ввода. */
  const values = { premises_type: premises?.premises_type ?? '' }
  FIELDS.forEach(({ key, decimal }) => {
    values[key] = shown(premises?.[key], decimal) ?? ''
  })
  return values
}

/* Пустое поле не отправляется вовсе: на сервере пустота означает «не знаю» и
   прежнего значения не стирает. Запятая приводится к точке - человек набирает
   площадь так, как пишет её в документах. */
function payloadOf(values) {
  const payload = {}
  FIELDS.forEach(({ key }) => {
    const value = String(values[key]).trim()
    if (value !== '') payload[key] = value.replace(',', '.')
  })
  if (values.premises_type) payload.premises_type = values.premises_type
  return payload
}

/* Вид собственности (07.10.2026, решение владельца): счёт без вида заводится
   обменом, а вид указывает человек. Пустой вид подсвечен: от него зависит, к
   кому требование, и без него судебный пакет не соберётся. */
function PremisesTypeCell({ value }) {
  if (value) return PREMISES_TYPE_TITLES[value] ?? value
  return <Tag kind="warn">не указан</Tag>
}

export default function AccountPremises({ accountId, premises, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState(() => valuesOf(premises))
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const start = () => {
    setValues(valuesOf(premises))
    setError(null)
    setEditing(true)
  }

  const save = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.savePremises(accountId, payloadOf(values))
      setEditing(false)
      onSaved()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section card">
      <h2 className="section__title">Помещение</h2>

      {editing ? (
        <form onSubmit={save}>
          <div className="row row--fields">
            <div className="field field--m">
              <span className="field__label" id="premises-type-label">
                Вид собственности
              </span>
              <Select
                value={values.premises_type}
                onChange={(value) => setValues({ ...values, premises_type: value })}
                options={PREMISES_TYPE_TITLES}
                empty="не указан"
                disabled={busy}
                labelledBy="premises-type-label"
              />
            </div>
            {FIELDS.map(({ key, label, placeholder }) => (
              <Field key={key} label={label} width="s">
                <input
                  className="field__input"
                  type="text"
                  value={values[key]}
                  placeholder={placeholder}
                  disabled={busy}
                  onChange={(event) => setValues({ ...values, [key]: event.target.value })}
                />
              </Field>
            ))}
          </div>

          <div className="muted">
            Эти сведения печатаются в финансово-лицевом счёте - он идёт в суд приложением
            к заявлению. Пустое поле означает «неизвестно» и прежнего значения не стирает.
          </div>

          <Notice error={error} />

          <div className="row row--tight">
            <button className="button" type="submit" disabled={busy}>
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </button>
            <button
              className="button button--quiet"
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Отмена
            </button>
          </div>
        </form>
      ) : (
        <>
          <table className="table table--pairs">
            <tbody>
              <tr>
                <td className="muted">Вид собственности</td>
                <td>
                  <PremisesTypeCell value={premises?.premises_type} />
                </td>
              </tr>
              {FIELDS.map(({ key, label, decimal }) => (
                <tr key={key}>
                  <td className="muted">{label}</td>
                  <td>
                    {shown(premises?.[key], decimal) ?? (
                      <span className="muted">нет в системе</span>
                    )}
                  </td>
                </tr>
              ))}

              {/* Доля показывается только когда она есть: править её руками
                  нельзя (приходит обменом), а строка «нет в системе» по всему
                  портфелю была бы шумом - документу эта графа пока не нужна. */}
              {premises?.share ? (
                <tr>
                  <td className="muted">Доля счёта</td>
                  <td>{shownShare(premises.share)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>

          {premises && !premises.premises_type ? (
            <Notice kind="warn">
              Вида собственности нет в вашем учёте. От него зависит, к кому требование:
              к собственнику или к нанимателю и членам его семьи. Пока вид не указан,
              счёт ждёт разбора, а судебный пакет по нему не собирается.
            </Notice>
          ) : null}

          {premises?.share ? (
            <div className="muted">
              Доля пришла обменом с 1С и показана для сведения: это доля лицевого счёта
              в помещении, а не доля человека. Требования по долям из неё не строятся -
              их состав задают собственники и наниматели счёта.
            </div>
          ) : null}

          {/* Кнопки нет вовсе у того, кому правка не положена: предлагать
              действие, в котором сервер заведомо откажет, значит обещать
              невозможное. Право то же, что на сборку пакета: эти значения
              уходят в документ для суда. */}
          {canEdit ? (
            <div className="row row--tight">
              <button type="button" className="button button--quiet" onClick={start}>
                Внести сведения
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}
